import { Copy, ExternalLink, FileText, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

type CmsStatus = 'Active' | 'Inactive'

type CmsPageRow = {
  id: number
  title: string
  slug: string
  content: string
  status: CmsStatus
  publicUrl: string
  updatedAt: string
}

type CmsForm = {
  id: number | null
  title: string
  slug: string
  content: string
  status: CmsStatus
}

const emptyForm: CmsForm = {
  id: null,
  title: '',
  slug: '',
  content: '',
  status: 'Active',
}

const landingBaseUrl = import.meta.env.VITE_LANDING_URL || 'http://localhost:5174'

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function publicUrl(slug: string) {
  return `${landingBaseUrl.replace(/\/$/, '')}/pages/${slug}`
}

export function CmsPage() {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [pages, setPages] = useState<CmsPageRow[]>([])
  const [form, setForm] = useState<CmsForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const loadPages = async () => {
    const { data } = await api.get('/admin/cms-pages')
    setPages(data.pages ?? [])
  }

  useEffect(() => {
    loadPages().catch(() => setError('Unable to load CMS pages.'))
  }, [])

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== form.content) {
      editorRef.current.innerHTML = form.content
    }
  }, [form.id, form.content])

  const startNew = () => {
    setForm(emptyForm)
    setMessage('')
    setError('')
    if (editorRef.current) editorRef.current.innerHTML = ''
  }

  const editPage = (page: CmsPageRow) => {
    setForm({
      id: page.id,
      title: page.title,
      slug: page.slug,
      content: page.content || '',
      status: page.status,
    })
    setMessage('')
    setError('')
  }

  const savePage = async () => {
    const content = editorRef.current?.innerHTML || form.content
    const payload = { ...form, content, slug: slugify(form.slug || form.title) }

    setSaving(true)
    setMessage('')
    setError('')
    try {
      const { data } = payload.id
        ? await api.put(`/admin/cms-pages/${payload.id}`, payload)
        : await api.post('/admin/cms-pages', payload)
      setForm({
        id: data.page.id,
        title: data.page.title,
        slug: data.page.slug,
        content: data.page.content,
        status: data.page.status,
      })
      await loadPages()
      setMessage('CMS page saved.')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save CMS page.')
    } finally {
      setSaving(false)
    }
  }

  const deletePage = async (page: CmsPageRow) => {
    if (!window.confirm(`Delete "${page.title}"?`)) return
    await api.delete(`/admin/cms-pages/${page.id}`)
    if (form.id === page.id) startNew()
    await loadPages()
  }

  const applyFormat = (command: string, value?: string) => {
    editorRef.current?.focus()
    document.execCommand(command, false, value)
    setForm(current => ({ ...current, content: editorRef.current?.innerHTML || '' }))
  }

  const insertLink = () => {
    const url = window.prompt('Enter URL')
    if (!url) return
    applyFormat('createLink', url)
  }

  const currentUrl = form.slug ? publicUrl(slugify(form.slug)) : ''

  return (
    <div className="space-y-6">
      <PageTitle
        title="CMS Pages"
        subtitle="Create content pages for Play Store, App Store, and landing-page links."
        action={<Button icon={Plus} onClick={startNew}>New Page</Button>}
      />

      <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
        <section className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="border-b border-stone-100 p-4">
            <h2 className="font-bold text-leaf-dark">Pages</h2>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {pages.length === 0 ? (
              <p className="p-4 text-sm text-stone-500">No CMS pages yet.</p>
            ) : pages.map(page => (
              <button
                className={`flex w-full gap-3 rounded-lg p-3 text-left transition hover:bg-mango-yellow/10 ${form.id === page.id ? 'bg-mango-yellow/15' : ''}`}
                key={page.id}
                onClick={() => editPage(page)}
                type="button"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-mango-yellow/20 text-leaf-dark">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-stone-900">{page.title}</span>
                  <span className="mt-1 block truncate text-xs text-stone-500">/pages/{page.slug}</span>
                  <span className="mt-2 inline-flex"><StatusPill value={page.status} /></span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-sm font-semibold text-stone-700">
              Page title
              <input
                className="rounded-lg border border-stone-200 px-3 py-2.5 outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
                value={form.title}
                onChange={(event) => setForm(current => ({
                  ...current,
                  title: event.target.value,
                  slug: current.id ? current.slug : slugify(event.target.value),
                }))}
                placeholder="Privacy Policy"
              />
            </label>
            <label className="grid gap-2 text-sm font-semibold text-stone-700">
              URL slug
              <input
                className="rounded-lg border border-stone-200 px-3 py-2.5 outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
                value={form.slug}
                onChange={(event) => setForm(current => ({ ...current, slug: slugify(event.target.value) }))}
                placeholder="privacy-policy"
              />
            </label>
          </div>

          <div className="mt-4 grid gap-2 text-sm font-semibold text-stone-700">
            Public URL
            <div className="flex flex-col gap-2 rounded-lg border border-stone-200 bg-stone-50 p-3 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 break-all text-xs font-semibold text-stone-600">
                {currentUrl || 'Add title or slug to generate URL'}
              </code>
              <div className="flex gap-2">
                <Button
                  icon={Copy}
                  onClick={() => currentUrl && navigator.clipboard?.writeText(currentUrl)}
                  variant="soft"
                  disabled={!currentUrl}
                >
                  Copy
                </Button>
                <a
                  className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold ${currentUrl ? 'bg-mango-yellow/20 text-leaf-dark hover:bg-mango-yellow/35' : 'pointer-events-none bg-stone-100 text-stone-400'}`}
                  href={currentUrl || '#'}
                  rel="noreferrer"
                  target="_blank"
                >
                  <ExternalLink className="h-4 w-4" /> Open
                </a>
              </div>
            </div>
          </div>

          <label className="mt-4 grid gap-2 text-sm font-semibold text-stone-700">
            Status
            <select
              className="max-w-xs rounded-lg border border-stone-200 px-3 py-2.5 outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
              value={form.status}
              onChange={(event) => setForm(current => ({ ...current, status: event.target.value as CmsStatus }))}
            >
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </label>

          <div className="mt-5 overflow-hidden rounded-xl border border-stone-200">
            <div className="flex flex-wrap gap-2 border-b border-stone-200 bg-stone-50 p-2">
              <button className="rounded-md px-3 py-1.5 text-sm font-bold hover:bg-white" onClick={() => applyFormat('bold')} type="button">B</button>
              <button className="rounded-md px-3 py-1.5 text-sm font-bold italic hover:bg-white" onClick={() => applyFormat('italic')} type="button">I</button>
              <button className="rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-white" onClick={() => applyFormat('formatBlock', 'h2')} type="button">H2</button>
              <button className="rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-white" onClick={() => applyFormat('insertUnorderedList')} type="button">List</button>
              <button className="rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-white" onClick={insertLink} type="button">Link</button>
            </div>
            <div
              ref={editorRef}
              className="min-h-[360px] overflow-y-auto bg-white p-5 text-sm leading-7 text-stone-700 outline-none focus:ring-4 focus:ring-mango-yellow/20 [&_a]:font-semibold [&_a]:text-leaf-dark [&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-xl [&_h2]:font-bold [&_li]:ml-5 [&_ul]:list-disc"
              contentEditable
              onInput={() => setForm(current => ({ ...current, content: editorRef.current?.innerHTML || '' }))}
              role="textbox"
              suppressContentEditableWarning
            />
          </div>

          {message && <p className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">{message}</p>}
          {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}

          <div className="mt-5 flex flex-wrap justify-between gap-3">
            {form.id ? (
              <Button
                icon={Trash2}
                onClick={() => {
                  const page = pages.find(item => item.id === form.id)
                  if (page) deletePage(page)
                }}
                variant="danger"
              >
                Delete
              </Button>
            ) : <span />}
            <Button icon={Save} onClick={savePage} disabled={saving}>
              {saving ? 'Saving...' : 'Save Page'}
            </Button>
          </div>
        </section>
      </div>
    </div>
  )
}
